import React, { useState, useEffect } from "react";
import { db } from "../firebaseConfig";
import { collection, getDocs, query, where } from "firebase/firestore";
import { useNavigate } from "react-router-dom"; 
import * as XLSX from "xlsx";
import Loader from "../components/Loader";

function EmpHistory() {
  const navigate = useNavigate();
  
  // 🔥 DATA STATES
  const [employees, setEmployees] = useState([]);
  const [selectedEmp, setSelectedEmp] = useState("");
  const [loading, setLoading] = useState(false);

  // 🔥 FILTER STATES
  const [viewMode, setViewMode] = useState('Month'); // 'Month', 'Year', 'All'
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7)); // YYYY-MM
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString()); // YYYY

  // Profile & Stats
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState({ 
      sales: 0, collection: 0, expenses: 0, advance: 0, 
      visits: 0, serviceCount: 0, presentDays: 0 
  });
  
  // Tab Data
  const [activeTab, setActiveTab] = useState("overview");
  const [salesData, setSalesData] = useState([]); 
  const [serviceData, setServiceData] = useState([]); 
  const [financeData, setFinanceData] = useState([]); 
  const [hrData, setHrData] = useState([]); 

  // Popup
  const [viewItem, setViewItem] = useState(null);

  // 1. FETCH EMPLOYEE LIST
  useEffect(() => {
    const fetchEmps = async () => {
        const q = await getDocs(collection(db, "users"));
        const list = [];
        q.forEach(doc => list.push(doc.data()));
        setEmployees(list.sort((a,b) => a.name.localeCompare(b.name)));
    };
    fetchEmps();
  }, []);

  // 2. FETCH COMPLETE KUNDALI
  const handleSearch = async () => {
    if(!selectedEmp) return alert("Select an Employee!");
    setLoading(true);

    try {
        // A. Profile (Fetch ID)
        const userProfile = employees.find(e => e.name === selectedEmp);
        setProfile(userProfile || { name: selectedEmp, role: "Unknown", empId: "N/A" });

        // B. Determine Filter Prefix based on View Mode
        let filterPrefix = "";
        if (viewMode === 'Month') filterPrefix = selectedMonth; 
        else if (viewMode === 'Year') filterPrefix = selectedYear; 
        else if (viewMode === 'All') filterPrefix = ""; 

        // Helper to check date match
        const isMatch = (dateStr) => (dateStr || "").startsWith(filterPrefix);

        // C. Define Queries
        const qOrders = query(collection(db, "orders"), where("userName", "==", selectedEmp));
        const qVisits = query(collection(db, "sales_reports"), where("userName", "==", selectedEmp));
        
        const qInstall = query(collection(db, "installations"), where("engineer", "==", selectedEmp));
        const qInstall2 = query(collection(db, "installations"), where("userName", "==", selectedEmp)); 

        const qService = query(collection(db, "service_reports"), where("userName", "==", selectedEmp));
        const qPMS = query(collection(db, "pms_reports"), where("userName", "==", selectedEmp));
        
        const qColl = query(collection(db, "payment_collections"), where("userName", "==", selectedEmp));
        const qExp = query(collection(db, "expenses"), where("userName", "==", selectedEmp));
        
        const qAdv = query(collection(db, "advances"), where("userName", "==", selectedEmp));
        const qAtt = query(collection(db, "attendance"), where("userName", "==", selectedEmp));
        const qLeave = query(collection(db, "leaves"), where("userName", "==", selectedEmp));

        // D. Parallel Fetch
        const [
            snapOrders, snapVisits, 
            snapInstall, snapInstall2, snapService, snapPMS,
            snapColl, snapExp, 
            snapAdv, snapAtt, snapLeave
        ] = await Promise.all([
            getDocs(qOrders), getDocs(qVisits),
            getDocs(qInstall), getDocs(qInstall2), getDocs(qService), getDocs(qPMS),
            getDocs(qColl), getDocs(qExp),
            getDocs(qAdv), getDocs(qAtt), getDocs(qLeave)
        ]);

        // E. Process Data
        let sList = [], svcList = [], finList = [], hList = [];
        let tSales=0, tColl=0, tExp=0, tAdv=0, tVisits=0, tSvc=0, tPres=0;

        // --- SALES TAB ---
        snapOrders.forEach(d => {
            const val = d.data();
            if(isMatch(val.date)) {
                tSales += Number(val.amount)||0;
                sList.push({...val, type: "🛒 Order", date: val.date});
            }
        });
        snapVisits.forEach(d => {
            const val = d.data();
            if(isMatch(val.createdAt)) {
                tVisits++;
                sList.push({...val, type: "🤝 Visit", date: val.createdAt});
            }
        });

        // --- SERVICE TAB ---
        const installDocs = [...snapInstall.docs, ...snapInstall2.docs];
        const uniqueInstalls = new Map();
        installDocs.forEach(d => uniqueInstalls.set(d.id, d.data()));

        uniqueInstalls.forEach(val => {
            if(isMatch(val.date)) {
                tSvc++;
                svcList.push({...val, type: "⚙️ Install", title: `Installed ${val.product}`});
            }
        });

        snapService.forEach(d => {
            const val = d.data();
            if(isMatch(val.date)) {
                tSvc++;
                svcList.push({...val, type: "🛠️ Repair", title: `Issue: ${val.issue}`});
            }
        });
        snapPMS.forEach(d => {
            const val = d.data();
            if(isMatch(val.date)) {
                tSvc++;
                svcList.push({...val, type: "🔧 PMS", title: val.activity || `Cycle: ${val.currentPmsNumber}`});
            }
        });

        // --- FINANCE TAB ---
        snapColl.forEach(d => {
            const val = d.data();
            if(isMatch(val.date)) {
                tColl += Number(val.amount)||0;
                finList.push({...val, type: "💰 Recvd", color: "green", desc: `From ${val.orgName}`});
            }
        });
        snapExp.forEach(d => {
            const val = d.data();
            if(isMatch(val.date)) {
                tExp += Number(val.amount)||0;
                finList.push({...val, type: "💸 Exp", color: "red", desc: `${val.type} - ${val.remark}`});
            }
        });

        // --- HR TAB ---
        snapAdv.forEach(d => {
            const val = d.data();
            if(isMatch(val.date)) {
                tAdv += Number(val.amount)||0;
                hList.push({...val, type: "💵 Advance", title: `Req: ₹${val.amount}`, status: val.status});
            }
        });
        snapAtt.forEach(d => {
            const val = d.data();
            if(isMatch(val.date)) {
                if(val.status === 'Present' || val.status === 'Half Day') tPres++;
                hList.push({...val, type: "📅 Attendance", title: `In: ${val.inTime}`, status: val.status});
            }
        });
        snapLeave.forEach(d => {
            const val = d.data();
            if(isMatch(val.fromDate)) {
                hList.push({...val, type: "🏖️ Leave", title: `${val.days} Days (${val.reason})`, status: val.status});
            }
        });

        // Sort Lists (Newest First)
        const sortFn = (a,b) => new Date(b.date||b.createdAt) - new Date(a.date||a.createdAt);
        setSalesData(sList.sort(sortFn));
        setServiceData(svcList.sort(sortFn));
        setFinanceData(finList.sort(sortFn));
        setHrData(hList.sort(sortFn));

        setStats({ 
            sales: tSales, collection: tColl, expenses: tExp, advance: tAdv,
            visits: tVisits, serviceCount: tSvc, presentDays: tPres
        });

    } catch (e) { console.error(e); alert("Error fetching data"); }
    setLoading(false);
  };

  // 3. EXCEL DOWNLOAD
  const downloadExcel = () => {
      let data = [];
      let sheet = "Data";
      if(activeTab === 'sales') { data = salesData; sheet="Sales"; }
      else if(activeTab === 'service') { data = serviceData; sheet="Service"; }
      else if(activeTab === 'finance') { data = financeData; sheet="Finance"; }
      else if(activeTab === 'hr') { data = hrData; sheet="HR_Records"; }
      else { return alert("Please select a tab to download"); }

      const timeLabel = viewMode === 'All' ? 'AllTime' : (viewMode === 'Year' ? selectedYear : selectedMonth);
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, sheet);
      XLSX.writeFile(wb, `${selectedEmp}_${timeLabel}_${sheet}.xlsx`);
  };

  const formatDate = (iso) => {
      if(!iso) return "-";
      const d = new Date(iso);
      return isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-GB');
  };

  // 4. YEAR OPTIONS
  const getYearOptions = () => {
      const current = new Date().getFullYear();
      const years = [];
      for(let y=2024; y<=current+2; y++) years.push(y);
      return years;
  };

  // 5. NAVIGATION HELPERS
  const goToLink = (type, value) => {
      if(!value) return;
      if(type === 'serial') {
          navigate('/asset-history', { state: { serialNo: value } });
      }
  };

  return (
    <div style={{padding:"20px", fontFamily:"'Segoe UI', sans-serif", maxWidth:"1200px", margin:"0 auto"}}>
      
      {/* HEADER */}
      <div style={searchBoxStyle}>
          <h2 style={{margin:0, color:"#2c3e50"}}>👤 Employee Performance</h2>
          
          <div style={{display:"flex", gap:"10px", marginTop:"15px", flexWrap:"wrap", alignItems:"center"}}>
              <select value={selectedEmp} onChange={(e)=>setSelectedEmp(e.target.value)} style={{...inputStyle, flex:1, minWidth:"200px"}}>
                  <option value="">-- Select Employee --</option>
                  {employees.map(e => <option key={e.email} value={e.name}>{e.name} ({e.role})</option>)}
              </select>

              {/* View Mode Toggles */}
              <div style={{display:"flex", background:"#eee", borderRadius:"5px", padding:"3px"}}>
                  {['Month', 'Year', 'All'].map(mode => (
                      <button key={mode} onClick={()=>setViewMode(mode)} 
                          style={{
                              padding:"8px 15px", border:"none", cursor:"pointer", borderRadius:"4px", fontSize:"13px", fontWeight:"bold",
                              background: viewMode===mode ? "white" : "transparent",
                              color: viewMode===mode ? "#333" : "#777",
                              boxShadow: viewMode===mode ? "0 2px 4px rgba(0,0,0,0.1)" : "none"
                          }}>
                          {mode}
                      </button>
                  ))}
              </div>

              {viewMode === 'Month' && <input type="month" value={selectedMonth} onChange={(e)=>setSelectedMonth(e.target.value)} style={inputStyle} />}
              
              {viewMode === 'Year' && (
                  <select value={selectedYear} onChange={(e)=>setSelectedYear(e.target.value)} style={inputStyle}>
                      {getYearOptions().map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
              )}

              {viewMode === 'All' && (
                  <div style={{padding:"10px", background:"#eee", borderRadius:"5px", fontSize:"13px", fontWeight:"bold", color:"#555"}}>
                      📅 All Time
                  </div>
              )}

              <button onClick={handleSearch} style={btnSearch}>Get Report</button>
          </div>
      </div>

      {loading && <Loader />}

      {profile && !loading && (
          <div>
              {/* PROFILE CARD (Fixed ID Display) */}
              <div style={profileCard}>
                  <div style={{flex:1, minWidth:"200px", borderRight:"1px solid #eee", paddingRight:"20px", marginBottom:"10px"}}>
                      <h2 style={{margin:0, color:"#2c3e50"}}>{profile.name}</h2>
                      
                      {/* 🔥🔥 ID & ROLE DISPLAY 🔥🔥 */}
                      <div style={{marginTop:"5px", marginBottom:"5px"}}>
                          <span style={{...roleBadge, backgroundColor:"#2c3e50", marginRight:"5px"}}>
                              {profile.empId || "ID: N/A"}
                          </span>
                          <span style={roleBadge}>{profile.role || "Employee"}</span>
                      </div>

                      <div style={{fontSize:"13px", color:"gray", lineHeight:"1.6"}}>
                          📞 {profile.mobile} <br/> 
                          {viewMode === 'Month' && <strong style={{color:"#27ae60"}}>🎯 Target: ₹{Number(profile.monthlyTarget||0).toLocaleString()}</strong>}
                      </div>
                  </div>
                  
                  {/* Stats Grid */}
                  <div style={{flex:3, display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(100px, 1fr))", gap:"10px"}}>
                      <div style={{...statBox, borderLeft:"4px solid #27ae60"}}>
                          <small>Sales</small>
                          <div style={{color:"#27ae60", fontWeight:"bold"}}>₹{stats.sales.toLocaleString()}</div>
                      </div>
                      <div style={{...statBox, borderLeft:"4px solid #2980b9"}}>
                          <small>Collected</small>
                          <div style={{color:"#2980b9", fontWeight:"bold"}}>₹{stats.collection.toLocaleString()}</div>
                      </div>
                      <div style={{...statBox, borderLeft:"4px solid #c0392b"}}>
                          <small>Expenses</small>
                          <div style={{color:"#c0392b", fontWeight:"bold"}}>₹{stats.expenses.toLocaleString()}</div>
                      </div>
                      <div style={{...statBox, borderLeft:"4px solid #e67e22"}}>
                          <small>Advance</small>
                          <div style={{color:"#e67e22", fontWeight:"bold"}}>₹{stats.advance.toLocaleString()}</div>
                      </div>
                      <div style={{...statBox, borderLeft:"4px solid #8e44ad"}}>
                          <small>Activity</small>
                          <div style={{color:"#8e44ad", fontWeight:"bold"}}>{stats.visits} V / {stats.serviceCount} S</div>
                      </div>
                      <div style={{...statBox, borderLeft:"4px solid #34495e"}}>
                          <small>Present</small>
                          <div style={{color:"#34495e", fontWeight:"bold"}}>{stats.presentDays} Days</div>
                      </div>
                  </div>
              </div>

              {/* TABS */}
              <div style={tabContainer}>
                  <button onClick={()=>setActiveTab("overview")} style={activeTab==="overview"?activeTabStyle:tabStyle}>📊 Overview</button>
                  <button onClick={()=>setActiveTab("sales")} style={activeTab==="sales"?{...activeTabStyle, color:"#27ae60", borderBottom:"3px solid #27ae60"}:tabStyle}>🛒 Sales ({salesData.length})</button>
                  <button onClick={()=>setActiveTab("service")} style={activeTab==="service"?{...activeTabStyle, color:"#c0392b", borderBottom:"3px solid #c0392b"}:tabStyle}>🛠️ Service ({serviceData.length})</button>
                  <button onClick={()=>setActiveTab("finance")} style={activeTab==="finance"?{...activeTabStyle, color:"#2980b9", borderBottom:"3px solid #2980b9"}:tabStyle}>💰 Finance ({financeData.length})</button>
                  <button onClick={()=>setActiveTab("hr")} style={activeTab==="hr"?{...activeTabStyle, color:"#8e44ad", borderBottom:"3px solid #8e44ad"}:tabStyle}>👔 HR ({hrData.length})</button>
                  
                  {activeTab !== 'overview' && <button onClick={downloadExcel} style={btnDownload}>📥 Excel</button>}
              </div>

              {/* CONTENT AREA */}
              <div style={contentArea}>
                  
                  {/* 1. OVERVIEW */}
                  {activeTab === "overview" && (
                      <div style={{textAlign:"center", padding:"30px", color:"gray"}}>
                          <p>Summary for <strong>{viewMode === 'All' ? 'All Time' : (viewMode==='Year'? selectedYear : selectedMonth)}</strong></p>
                          {viewMode === 'Month' && profile.monthlyTarget > 0 && (
                              <div style={{marginTop:"15px", padding:"15px", background:"#f0fdf4", display:"inline-block", borderRadius:"8px", border:"1px solid #bbf7d0"}}>
                                  <span style={{fontSize:"24px", color:"green", fontWeight:"bold"}}>
                                      {((stats.sales/profile.monthlyTarget)*100).toFixed(0)}%
                                  </span>
                                  <div style={{fontSize:"12px"}}>Target Achieved</div>
                              </div>
                          )}
                      </div>
                  )}

                  {/* 2. SALES TAB */}
                  {activeTab === "sales" && (
                      <table style={tableStyle}>
                          <thead><tr style={{background:"#f0fdf4"}}><th style={thStyle}>Date</th><th style={thStyle}>Activity</th><th style={thStyle}>Client</th><th style={thStyle}>Details</th><th style={thStyle}>Amount/Status</th></tr></thead>
                          <tbody>
                              {salesData.map((s, i) => (
                                  <tr key={i}>
                                      <td style={tdStyle}>{formatDate(s.date)}</td>
                                      <td style={tdStyle}>
                                          <span style={{padding:"2px 6px", borderRadius:"4px", fontSize:"11px", fontWeight:"bold", color:"white", background: s.type==='🛒 Order'?'green':'blue'}}>
                                              {s.type}
                                          </span>
                                      </td>
                                      <td style={tdStyle}>{s.hospital || s.hospitalName || s.orgName}</td>
                                      <td style={tdStyle}>{s.productDetails || s.outcome}</td>
                                      <td style={tdStyle}>{s.amount ? `₹${s.amount}` : s.person}</td>
                                  </tr>
                              ))}
                              {salesData.length===0 && <tr><td colSpan="5" style={{textAlign:"center", padding:"10px"}}>No Data</td></tr>}
                          </tbody>
                      </table>
                  )}

                  {/* 3. SERVICE TAB */}
                  {activeTab === "service" && (
                      <table style={tableStyle}>
                          <thead><tr style={{background:"#fff1f2"}}><th style={thStyle}>Date</th><th style={thStyle}>Type</th><th style={thStyle}>Client / Serial</th><th style={thStyle}>Details</th><th style={thStyle}>Action</th></tr></thead>
                          <tbody>
                              {serviceData.map((s, i) => (
                                  <tr key={i}>
                                      <td style={tdStyle}>{formatDate(s.date || s.createdAt)}</td>
                                      <td style={tdStyle}>
                                          <span style={{padding:"2px 6px", borderRadius:"4px", fontSize:"11px", fontWeight:"bold", color:"white", background: s.type.includes('Install')?'#2c3e50':(s.type.includes('PMS')?'#8e44ad':'#c0392b')}}>
                                              {s.type}
                                          </span>
                                      </td>
                                      <td style={tdStyle}>
                                          <div>{s.hospital || s.hospitalName}</div>
                                          {s.serialNo && <small style={{color:"gray"}}>SN: {s.serialNo}</small>}
                                      </td>
                                      <td style={tdStyle}>{s.title}</td>
                                      <td style={tdStyle}>
                                          <button onClick={()=>setViewItem(s)} style={btnView}>👁️</button>
                                          {s.serialNo && <button onClick={()=>goToLink('serial', s.serialNo)} style={linkIcon}>🚀</button>}
                                      </td>
                                  </tr>
                              ))}
                              {serviceData.length===0 && <tr><td colSpan="5" style={{textAlign:"center", padding:"10px"}}>No Data</td></tr>}
                          </tbody>
                      </table>
                  )}

                  {/* 4. FINANCE TAB */}
                  {activeTab === "finance" && (
                      <table style={tableStyle}>
                          <thead><tr style={{background:"#eff6ff"}}><th style={thStyle}>Date</th><th style={thStyle}>Type</th><th style={thStyle}>Description</th><th style={thStyle}>Amount</th><th style={thStyle}>Info</th></tr></thead>
                          <tbody>
                              {financeData.map((f, i) => (
                                  <tr key={i}>
                                      <td style={tdStyle}>{formatDate(f.date)}</td>
                                      <td style={{...tdStyle, color: f.color, fontWeight:"bold"}}>{f.type}</td>
                                      <td style={tdStyle}>{f.desc}</td>
                                      <td style={{...tdStyle, fontWeight:"bold", color: f.color}}>₹ {f.amount}</td>
                                      <td style={tdStyle}><button onClick={()=>setViewItem(f)} style={btnView}>👁️</button></td>
                                  </tr>
                              ))}
                              {financeData.length===0 && <tr><td colSpan="5" style={{textAlign:"center", padding:"10px"}}>No Data</td></tr>}
                          </tbody>
                      </table>
                  )}

                  {/* 5. HR TAB */}
                  {activeTab === "hr" && (
                      <table style={tableStyle}>
                          <thead><tr style={{background:"#f3e8ff"}}><th style={thStyle}>Date</th><th style={thStyle}>Activity</th><th style={thStyle}>Details</th><th style={thStyle}>Status</th></tr></thead>
                          <tbody>
                              {hrData.map((h, i) => (
                                  <tr key={i}>
                                      <td style={tdStyle}>{formatDate(h.date || h.fromDate)}</td>
                                      <td style={tdStyle}><strong>{h.type}</strong></td>
                                      <td style={tdStyle}>{h.title} {h.reason ? `- ${h.reason}` : ''}</td>
                                      <td style={{...tdStyle, fontWeight:"bold", color: h.status==='Approved'||h.status==='Present'?'green':'orange'}}>{h.status || h.outTime}</td>
                                  </tr>
                              ))}
                              {hrData.length===0 && <tr><td colSpan="4" style={{textAlign:"center", padding:"10px"}}>No Data</td></tr>}
                          </tbody>
                      </table>
                  )}

              </div>
          </div>
      )}

      {/* POPUP MODAL */}
      {viewItem && (
          <div style={modalOverlay}>
              <div style={modalContent}>
                  <h3 style={{marginTop:0, color:"#3b5998", borderBottom:"1px solid #eee", paddingBottom:"10px"}}>ℹ️ Details</h3>
                  <div style={{fontSize:"13px", lineHeight:"1.6"}}>
                      <table style={{width:"100%", borderCollapse:"collapse"}}>
                          <tbody>
                              {Object.entries(viewItem).map(([key, value]) => {
                                  if(key==='id' || typeof value === 'object' || !value) return null;
                                  return (
                                      <tr key={key} style={{borderBottom:"1px solid #f0f0f0"}}>
                                          <td style={{fontWeight:"bold", color:"#555", textTransform:"capitalize", padding:"5px 0", width:"40%"}}>{key.replace(/([A-Z])/g, ' $1')}</td>
                                          <td style={{padding:"5px 0", textAlign:"right"}}>{value.toString()}</td>
                                      </tr>
                                  );
                              })}
                          </tbody>
                      </table>
                  </div>
                  <button onClick={()=>setViewItem(null)} style={btnClose}>Close</button>
              </div>
          </div>
      )}

    </div>
  );
}

// STYLES
const searchBoxStyle = { textAlign:"center", background:"white", padding:"20px", borderRadius:"10px", boxShadow:"0 4px 10px rgba(0,0,0,0.1)", marginBottom:"20px" };
const inputStyle = { padding:"10px", borderRadius:"5px", border:"1px solid #ccc", fontSize:"14px", margin:"0 5px" };
const btnSearch = { padding:"10px 20px", backgroundColor:"#2c3e50", color:"white", border:"none", borderRadius:"5px", cursor:"pointer", fontWeight:"bold" };
const btnDownload = { padding:"5px 15px", backgroundColor:"#27ae60", color:"white", border:"none", borderRadius:"5px", cursor:"pointer", fontSize:"12px", marginLeft:"auto" };

const profileCard = { background:"white", padding:"20px", borderRadius:"10px", boxShadow:"0 2px 5px rgba(0,0,0,0.1)", display:"flex", flexWrap:"wrap", marginBottom:"20px" };
const roleBadge = { background:"#3498db", color:"white", padding:"2px 8px", borderRadius:"10px", fontSize:"11px", fontWeight:"bold" };
const statBox = { textAlign:"center", background:"#f8f9fa", padding:"10px", borderRadius:"8px", minWidth:"90px" };

const tabContainer = { display:"flex", borderBottom:"2px solid #ddd", marginBottom:"20px", overflowX:"auto", whiteSpace:"nowrap" };
const tabStyle = { padding:"12px 15px", background:"none", border:"none", cursor:"pointer", fontSize:"13px", color:"#555", fontWeight:"bold" };
const activeTabStyle = { ...tabStyle, borderBottom:"3px solid #3b5998", color:"#3b5998", background:"#f0f9ff" };

const contentArea = { background:"white", padding:"20px", borderRadius:"10px", boxShadow:"0 2px 5px rgba(0,0,0,0.05)", minHeight:"300px", overflowX:"auto" };

const tableStyle={width:"100%", borderCollapse:"collapse", fontSize:"13px"};
const thStyle={padding:"10px", border:"1px solid #ddd", textAlign:"left", background:"#f8fafc"};
const tdStyle={padding:"8px 10px", border:"1px solid #ddd", verticalAlign:"middle"};

const linkIcon = {background:"none", border:"none", cursor:"pointer", marginLeft:"5px", fontSize:"14px"};
const btnView = {padding:"2px 6px", cursor:"pointer", fontSize:"14px", background:"none", border:"none"};

const modalOverlay = { position:"fixed", top:0, left:0, width:"100%", height:"100%", background:"rgba(0,0,0,0.5)", display:"flex", justifyContent:"center", alignItems:"center", zIndex:1000 };
const modalContent = { background:"white", padding:"25px", borderRadius:"10px", width:"450px", boxShadow:"0 5px 15px rgba(0,0,0,0.3)", maxHeight:"80vh", overflowY:"auto" };
const btnClose = { marginTop:"15px", backgroundColor:"#333", color:"white", border:"none", padding:"10px", borderRadius:"4px", width:"100%", cursor:"pointer" };

export default EmpHistory;